import { Bell, LogOut, User, ChevronDown, ChevronRight, Building2 } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { useTenant } from '@/hooks/use-tenant'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'

export function Header() {
  const { user, logout } = useAuth()
  const { activeTenantName, tenants, canSwitch, switchTenant, activeTenantId } = useTenant()
  const routerState = useRouterState()
  const navigate = useNavigate()
  const isAdminPage = routerState.location.pathname.startsWith('/admin')
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on click outside
  useEffect(() => {
    if (!showDropdown) return
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showDropdown])

  return (
    <header
      className="sticky top-0 z-30 flex h-16 items-center justify-between border-b px-6"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* Left: Tenant switcher or title */}
      <div className="flex items-center gap-3">
        {canSwitch && !isAdminPage ? (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <Building2 className="h-4 w-4" style={{ color: 'var(--color-brand-secondary)' }} />
              <span>{activeTenantName}</span>
              <ChevronDown className="h-4 w-4" style={{ color: 'var(--color-text-tertiary)' }} />
            </button>

            {showDropdown && (
              <div
                className="absolute left-0 top-full mt-1 w-64 rounded-lg border py-1"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 50,
                }}
              >
                <p
                  className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  Cambiar cliente
                </p>
                <div className="max-h-64 overflow-y-auto">
                  {tenants.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        switchTenant(t.id)
                        setShowDropdown(false)
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50"
                      style={{
                        color: t.id === activeTenantId ? 'var(--color-brand-secondary)' : 'var(--color-text-primary)',
                        fontWeight: t.id === activeTenantId ? 600 : 400,
                      }}
                    >
                      <Building2
                        className="h-4 w-4 shrink-0"
                        style={{
                          color: t.id === activeTenantId ? 'var(--color-brand-secondary)' : 'var(--color-text-tertiary)',
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{t.name}</p>
                        <p className="truncate text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                          {t.slug}
                        </p>
                      </div>
                      {t.id === activeTenantId && (
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: 'var(--color-brand-secondary)' }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {isAdminPage ? 'ROI365' : (activeTenantName || 'ROI365')}
          </h1>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <button
          className="relative rounded-md p-2 transition-colors hover:bg-gray-100"
          title="Notificaciones"
        >
          <Bell className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
        </button>

        {/* User menu */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate({ to: '/profile' })}
            className="user-menu-btn group flex cursor-pointer items-center gap-3 rounded-lg border px-2.5 py-1.5 transition-all hover:shadow-sm"
            title="Ver mi perfil"
            aria-label="Ir a mi perfil"
          >
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: 'var(--color-brand-secondary)' }}
            >
              {user?.name?.charAt(0).toUpperCase() ?? <User className="h-4 w-4" />}
            </div>
            <div className="hidden text-left md:block">
              <p className="text-sm font-medium leading-tight" style={{ color: 'var(--color-text-primary)' }}>
                {user?.name}
              </p>
              <p className="text-xs leading-tight" style={{ color: 'var(--color-text-tertiary)' }}>
                {user?.role === 'super_admin' ? 'Administrador Global' : user?.tenant_name}
              </p>
            </div>
            <ChevronRight
              className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
              style={{ color: 'var(--color-text-tertiary)' }}
            />
          </button>
          <button
            onClick={() => logout()}
            className="rounded-md p-2 transition-colors hover:bg-gray-100"
            title="Cerrar sesion"
            aria-label="Cerrar sesion"
          >
            <LogOut className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
          </button>
        </div>
      </div>
    </header>
  )
}
